import { useEffect, useMemo, useState } from "react";
import PageHeader from "../components/layout/PageHeader";
import api, { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "../components/ui/select";
import { CheckCircle2, Trophy, Calendar, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const todayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

export default function Results() {
  const [games, setGames] = useState([]);
  const [operators, setOperators] = useState([]);
  const [results, setResults] = useState([]);
  const [selectedOperator, setSelectedOperator] = useState("");
  const [gameId, setGameId] = useState("");
  const [drawDate, setDrawDate] = useState(todayStr());
  const [first, setFirst] = useState("");
  const [second, setSecond] = useState("");
  const [third, setThird] = useState("");
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const load = async () => {
    const [g, o, r] = await Promise.all([
      api.get("/games"),
      api.get("/operators"),
      api.get("/draw-results", { params: { draw_date: drawDate } }),
    ]);
    setGames(g.data);
    setOperators(o.data);
    setResults(r.data);
  };

  useEffect(() => { load(); }, [drawDate]); // eslint-disable-line

  const game = useMemo(() => games.find((g) => g.id === gameId), [games, gameId]);
  const filteredGames = useMemo(
    () => games.filter((g) => g.active && (!selectedOperator || g.operator_id === selectedOperator)),
    [games, selectedOperator]
  );

  const clean = (v) => v.replace(/\D/g, "");
  const maxLen = game?.number_digits || 2;

  const submit = async () => {
    if (!gameId) return toast.error("Selecciona un juego");
    if (!first || first.length !== maxLen) return toast.error(`Primer lugar debe tener ${maxLen} dígitos`);
    setSaving(true);
    try {
      const payload = {
        game_id: gameId,
        date: drawDate,
        first,
        second: second || null,
        third: third || null,
      };
      await api.post("/draw-results", payload);
      toast.success("Resultado guardado y tickets evaluados");
      setFirst(""); setSecond(""); setThird("");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    setSaving(false);
  };

  const syncAll = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/draw-results/sync", null, { params: { all_games: true, draw_date: drawDate } });
      toast.success(`Sincronizados ${data.synced} de ${data.total} juegos`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    setSyncing(false);
  };

  return (
    <div className="flex-1 bg-slate-50 min-h-full">
      <PageHeader
        title="Resultados de Sorteo"
        subtitle="Ingreso manual de resultados · Evaluación automática de tickets"
        testId="results-header"
        actions={
          <Button
            onClick={syncAll}
            disabled={syncing}
            variant="outline"
            className="rounded-sm border-slate-300 font-semibold"
            data-testid="results-sync-all"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${syncing ? "animate-spin" : ""}`} />
            {syncing ? "Sincronizando…" : "Sincronizar resultados"}
          </Button>
        }
      />

      <div className="p-6 sm:p-8 grid grid-cols-1 xl:grid-cols-5 gap-6">
        {/* Entry form */}
        <div className="xl:col-span-2">
          <div className="bg-white border border-slate-200 rounded-sm p-6 sticky top-24">
            <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500 mb-4">
              Registrar resultado
            </div>

            <div className="space-y-4">
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-700">Fecha</Label>
                <div className="relative mt-1">
                  <Calendar className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input type="date" value={drawDate} onChange={(e) => setDrawDate(e.target.value)} className="pl-9 rounded-sm font-mono" data-testid="result-date" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-slate-700">Operador</Label>
                  <Select value={selectedOperator} onValueChange={(v) => { setSelectedOperator(v); setGameId(""); }}>
                    <SelectTrigger className="mt-1 rounded-sm" data-testid="result-operator"><SelectValue placeholder="Todos" /></SelectTrigger>
                    <SelectContent>
                      {operators.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-slate-700">Juego</Label>
                  <Select value={gameId} onValueChange={setGameId}>
                    <SelectTrigger className="mt-1 rounded-sm" data-testid="result-game"><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                    <SelectContent>
                      {filteredGames.map((g) => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-700">
                  Números ganadores · {maxLen} dígitos
                </Label>
                <div className="flex gap-3 mt-2">
                  {[
                    { v: first, s: setFirst, lbl: "1ro", key: "1st" },
                    { v: second, s: setSecond, lbl: "2do", key: "2nd" },
                    { v: third, s: setThird, lbl: "3ro", key: "3rd" },
                  ].map((n) => (
                    <div key={n.key} className="flex-1">
                      <Input
                        value={n.v}
                        onChange={(e) => n.s(clean(e.target.value).slice(0, maxLen))}
                        className="h-16 text-3xl text-center font-mono font-black border-2 border-slate-300 rounded-sm focus:border-blue-600 focus:ring-2 focus:ring-blue-600 bg-slate-50 focus:bg-white"
                        placeholder={"0".repeat(maxLen)}
                        inputMode="numeric"
                        data-testid={`result-${n.key}`}
                      />
                      <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 text-center mt-1">{n.lbl}</div>
                    </div>
                  ))}
                </div>
              </div>

              <Button
                onClick={submit}
                disabled={saving}
                className="w-full h-12 bg-blue-600 hover:bg-blue-700 rounded-sm font-bold"
                data-testid="result-submit"
              >
                {saving ? "Guardando…" : "Publicar resultado y evaluar tickets"}
              </Button>

              <div className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-sm p-3">
                ⚠ Al publicar, todos los tickets activos del juego/fecha serán evaluados
                automáticamente y los ganadores quedarán habilitados para cobro.
              </div>
            </div>
          </div>
        </div>

        {/* Results list */}
        <div className="xl:col-span-3">
          <div className="bg-white border border-slate-200 rounded-sm">
            <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Trophy className="w-4 h-4 text-slate-600" />
                <div className="text-sm font-bold text-slate-900">
                  Resultados del <span className="font-mono">{drawDate}</span>
                </div>
              </div>
              <span className="text-xs font-mono text-slate-500">{results.length} registrados</span>
            </div>
            {results.length === 0 ? (
              <div className="p-12 text-center text-sm text-slate-500">Sin resultados para esta fecha</div>
            ) : (
              <ul className="divide-y divide-slate-100">
                {results.map((r) => (
                  <li key={r.id} className="px-5 py-4 flex items-center justify-between hover:bg-slate-50" data-testid={`result-row-${r.game_id}`}>
                    <div>
                      <div className="text-sm font-bold text-slate-950">{r.game_name}</div>
                      <div className="text-xs text-slate-500 font-mono">{r.date}</div>
                    </div>
                    <div className="flex gap-2">
                      {[
                        { v: r.first, lbl: "1ro" },
                        { v: r.second, lbl: "2do" },
                        { v: r.third, lbl: "3ro" },
                      ].filter(x => x.v).map((n) => (
                        <div key={n.lbl} className="text-center">
                          <div className="text-xl font-black font-mono bg-green-50 text-green-800 border-2 border-green-200 rounded-sm px-3 py-1 tabular-nums">
                            {n.v}
                          </div>
                          <div className="text-[9px] font-bold uppercase tracking-widest text-slate-400 mt-0.5">{n.lbl}</div>
                        </div>
                      ))}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {results.length > 0 && (
            <div className="mt-4 flex items-center gap-2 text-xs text-slate-600 px-3">
              <CheckCircle2 className="w-3 h-3 text-green-600" />
              Los tickets han sido evaluados automáticamente. Ve a <b>Pagos</b> para cobrar premios.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
